import { useEffect, useRef, useState } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { ArrowCounterClockwiseIcon, ArrowClockwiseIcon } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import ModalCloseButton from '../../../components/generic/ModalCloseButton';
import { getCroppedWebpBlob, rotateImage90 } from './imageProcessing';

const ImageCropperModal = ({ imageSrc, onCancel, onConfirm }) => {
  const [workingSrc, setWorkingSrc] = useState(imageSrc);
  const [crop, setCrop] = useState();
  const [pixelCrop, setPixelCrop] = useState(null);
  const [isRotating, setIsRotating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const imageRef = useRef(null);

  // Default the crop rectangle to the full image so it isn't hidden behind an
  // arbitrary small box — the user can then drag any edge/corner to resize it.
  const handleImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    setCrop(centerCrop(makeAspectCrop({ unit: '%', width: 100 }, width / height, width, height), width, height));
  };

  useEffect(
    () => () => {
      if (workingSrc !== imageSrc) URL.revokeObjectURL(workingSrc);
    },
    [workingSrc, imageSrc],
  );

  const handleRotate = async (direction) => {
    setIsRotating(true);
    try {
      const rotatedSrc = await rotateImage90(workingSrc, direction);
      if (workingSrc !== imageSrc) URL.revokeObjectURL(workingSrc);
      setWorkingSrc(rotatedSrc);
      setPixelCrop(null);
    } finally {
      setIsRotating(false);
    }
  };

  const handleConfirm = async () => {
    if (!pixelCrop?.width || !pixelCrop?.height || !imageRef.current) return;
    setIsProcessing(true);
    try {
      // react-image-crop reports the crop rect in on-screen pixels (relative to
      // the rendered <img>), so scale it up to the image's natural resolution.
      const { naturalWidth, naturalHeight, width, height } = imageRef.current;
      const scaleX = naturalWidth / width;
      const scaleY = naturalHeight / height;
      const fullResCrop = {
        x: pixelCrop.x * scaleX,
        y: pixelCrop.y * scaleY,
        width: pixelCrop.width * scaleX,
        height: pixelCrop.height * scaleY,
      };

      const blob = await getCroppedWebpBlob(workingSrc, fullResCrop);
      onConfirm(blob);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden" showCloseButton={false}>
        <ModalCloseButton onClick={onCancel} />
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle>Adjust image</DialogTitle>
        </DialogHeader>

        <div className="relative w-full h-[420px] flex items-center justify-center bg-black overflow-hidden">
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={setPixelCrop}
            className="min-w-0 max-w-full max-h-full"
          >
            <img
              ref={imageRef}
              src={workingSrc}
              alt="Selected solution"
              onLoad={handleImageLoad}
              className="block w-auto h-auto object-contain"
              // react-image-crop's own stylesheet sets `.ReactCrop__child-wrapper>img
              // { max-height: inherit }`, which beats a Tailwind max-h-* class on
              // specificity and resolves to no cap. Inline style always wins instead.
              style={{ maxWidth: '100%', maxHeight: 420, opacity: isRotating ? 0.5 : 1 }}
            />
          </ReactCrop>
        </div>

        <div className="flex flex-row items-center justify-center gap-4 px-6 py-4">
          <span className="text-[13px] text-[var(--q3-neutral-light)]">Rotate</span>
          <button
            type="button"
            disabled={isRotating}
            onClick={() => handleRotate('ccw')}
            className="flex items-center justify-center h-8 w-8 rounded-full border border-[var(--q3-stroke-normal)] disabled:opacity-50"
          >
            <ArrowCounterClockwiseIcon size={16} />
          </button>
          <button
            type="button"
            disabled={isRotating}
            onClick={() => handleRotate('cw')}
            className="flex items-center justify-center h-8 w-8 rounded-full border border-[var(--q3-stroke-normal)] disabled:opacity-50"
          >
            <ArrowClockwiseIcon size={16} />
          </button>
        </div>

        <div className="flex flex-row gap-3 px-6 pb-6">
          <Button variant="outline" className="flex-1 rounded-full" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            className="flex-1 rounded-full"
            onClick={handleConfirm}
            disabled={isProcessing || isRotating || !pixelCrop?.width}
          >
            {isProcessing ? 'Processing…' : 'Use Image'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropperModal;
